/* eslint-disable no-param-reassign,no-constant-condition */
import { curry, type PlatformEvent } from 'lib/util'
import { BLE_PERIPHERAL_CONNECTION_STATUS } from 'model/_shared/collection/bluetooth/constants'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import settings from '@tenant/settings'
import { actionWithHooks } from 'lib/sequence/module/action'

const { bluetooth }: { bluetooth: any } = settings

/**
 * Displays a dialog waiting for the user to confirm the pairing
 *
 * @param module      current module
 * @param isConnected whether the device is connected
 */
const showConfirmDialog = async (
  module: Gaia.AppModule.Spec,
  isConnected = false,
): Promise<any> => await showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: `dialog.${isConnected ? 'unpairDevice' : 'pairDevice'}.title`,
    defaultValue: isConnected ? 'Disconnect' : 'Connect',
  },
  text: {
    ns: 'apmGo',
    key: `dialog.${isConnected ? 'unpairDevice' : 'pairDevice'}.text`,
    defaultValue: isConnected ? 'Do you want to disconnect from the peripheral?' : 'Do you want to connect to the peripheral?',
  },
  ok: {
    ns: 'common',
    key: 'button.yes',
    defaultValue: 'Yes',
  },
  cancel: {
    ns: 'common',
    key: 'button.cancel',
    defaultValue: 'Cancel',
  },
})

const showErrorDialog = async (
  module: Gaia.AppModule.Spec,
  isConnected: boolean,
): Promise<any> => await showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: `dialog.${isConnected ? 'unpairDeviceError' : 'pairDeviceError'}.title`,
    defaultValue: `Device could not be ${isConnected ? 'unpaired' : 'paired'}`,
  },
  text: {
    ns: 'apmGo',
    key: `dialog.${isConnected ? 'unpairDeviceError' : 'pairDeviceError'}.text`,
    defaultValue: 'Please check that the device is turned on and within range, then try again.',
  },
  ok: {
    ns: 'common',
    key: 'button.retry',
    defaultValue: 'Retry',
  },
  cancel: {
    ns: 'common',
    key: 'button.cancel',
    defaultValue: 'Cancel',
  },
})

const togglePeripheralConnection = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec & { loading: boolean },
  event: PlatformEvent,
): Promise<void> => {
  const { sensor: model } = module[G.MODEL][G.CHILDREN]
  const { value: { name: peripheralName } = {} } = model[G.CACHE]
  const { [G.BLUETOOTH]: bluetoothAdapter } = module[G.ADAPTER]

  const peripheral = bluetoothAdapter[G.GROUP]?.[peripheralName] || {}
  const {
    connectionStatus = BLE_PERIPHERAL_CONNECTION_STATUS.DISCONNECTED
  } = peripheral?.[G.STATE] || {}
  const isConnected = connectionStatus === BLE_PERIPHERAL_CONNECTION_STATUS.CONNECTED

  if (await showConfirmDialog(module, isConnected)) {
    const adapterFn = isConnected ? 'disconnect' : 'connect'

    while (true) {
      try {
        component.loading = true
        await module[G.ADAPTER][G.UI].update(module)
        await bluetoothAdapter[G.API][adapterFn]({
          detail: {
            [G.DATA]: {
              name: peripheralName,
              item: peripheral,
              service: bluetooth?.constants.apm.service,
              characteristics: bluetooth?.constants.apm.characteristics,
            },
          },
        })

        component.loading = false
        await actionWithHooks(module[G.STATE][G.ACTION])([])
        break
      } catch (e) {
        component.loading = false
        await module[G.ADAPTER][G.UI].update(module)
        if (!await showErrorDialog(module, isConnected)) {
          throw e
        }
      }
    }
  }
}

export default curry(togglePeripheralConnection)
